// =============================================================================
// src/utils/leadValidation.ts
// Client-side validation + sanitization for PUBLIC lead/demo form submissions.
// This is defense-in-depth + UX: the real backstop is the DB CHECK constraints
// (migration 20260621001818_harden_public_lead_inputs) since anon inserts can
// bypass the client. Keep the bounds here in sync with that migration.
// No Supabase imports — safe in the public bundle.
// =============================================================================

import { z } from "zod";
import { LEAD_LANGUAGES } from "./leadStatus";

// Drop control chars (codepoint < 32 and DEL) and collapse whitespace. Built by
// codepoint test rather than a regex class so no control byte lives in source.
function stripControl(input: string, keepNewlines: boolean): string {
  let out = "";
  for (const ch of input) {
    const c = ch.codePointAt(0)!;
    if (keepNewlines && (c === 10 || c === 13)) {
      out += ch;
    } else if (c >= 32 && c !== 127) {
      out += ch;
    } else {
      out += " ";
    }
  }
  return out.replace(/[ \t]+/g, " ").trim();
}

// Single-line text: no control chars, collapsed whitespace.
const clean = (max: number) =>
  z
    .string()
    .transform((s) => stripControl(s, false))
    .pipe(z.string().max(max));

// Free-form text keeps newlines (textarea); only drops other control chars.
const cleanMultiline = (max: number) =>
  z
    .string()
    .transform((s) => stripControl(s, true))
    .pipe(z.string().max(max));

const emailField = z
  .string()
  .transform((s) => s.trim().toLowerCase())
  .pipe(z.string().email().max(254));

const optEmail = z
  .literal("")
  .transform(() => undefined)
  .or(emailField)
  .optional();

const langField = z.enum(LEAD_LANGUAGES as [string, ...string[]]);

// ── .360 tour leads ─────────────────────────────────────────────────────────
export const tourLeadSchema = z.object({
  tour_id: z.string().uuid(),
  session_id: z.string().uuid().nullish(),
  name: clean(120).optional(),
  email: optEmail,
  phone: clean(40).optional(),
  message: cleanMultiline(2000).optional(),
  preferred_language: langField.optional(),
  open_house_event_id: z.string().uuid().nullish(),
});

// ── .tap venue leads ──────────────────────────────────────────────────────────
export const venueLeadSchema = z.object({
  venue_id: z.string().uuid(),
  branch_id: z.string().uuid().nullish(),
  staff_id: z.string().uuid().nullish(),
  name: clean(120).optional(),
  email: optEmail,
  phone: clean(40).optional(),
  message: cleanMultiline(2000).optional(),
  preferred_language: langField.optional(),
  open_house_event_id: z.string().uuid().nullish(),
});

// ── Demo requests (email required) ────────────────────────────────────────────
export const demoRequestSchema = z.object({
  email: emailField,
  kind: z.enum(["demo", "call", "quote"]).optional(),
  name: clean(120).optional(),
  phone: clean(40).optional(),
  company: clean(160).optional(),
  product: z.enum(["360", "tap", "both"]).optional(),
  preferred_date: z.string().nullish(),
  preferred_at: z.string().datetime({ offset: true }).nullish(),
  message: cleanMultiline(2000).optional(),
  locale: clean(10).optional(),
  preferred_language: langField.optional(),
  source: clean(80).optional(),
});
